import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { NavLink } from 'react-router-dom';
import { FiHome, FiSearch, FiBookmark, FiChevronDown, FiChevronUp, FiUser, FiMenu, FiX } from 'react-icons/fi';

const SidebarMenuItem = ({ item, hideIcon = false, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasSubmenu = item.submenu && item.submenu.length > 0;

  const toggleSubmenu = (e) => {
    if (hasSubmenu) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleLinkClick = () => {
    if (onClose) {
      onClose();
    }
  };

  const MainLink = ({ children }) =>
    hasSubmenu ? (
      <div
        onClick={toggleSubmenu}
        className="flex items-center justify-between gap-4 w-full px-4 py-3 cursor-pointer text-brand-light-gray hover:bg-brand-gray rounded-lg transition-colors mx-2"
      >
        {children}
      </div>
    ) : (
      <NavLink
        to={item.path}
        onClick={handleLinkClick}
        className={({ isActive }) =>
          `flex items-center gap-4 w-full px-4 py-3 rounded-lg transition-colors duration-300 mx-2 ${isActive ? 'bg-white text-brand-dark font-semibold shadow-lg' : 'text-brand-light-gray hover:bg-brand-gray'
          }`
        }
      >
        {children}
      </NavLink>
    );

  return (
    <div className="w-full">
      <MainLink>
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-4">
            {!hideIcon && item.icon && <item.icon size={20} />}
            <span className="font-semibold">{item.name}</span>
          </div>
          {hasSubmenu && (isOpen ? <FiChevronUp /> : <FiChevronDown />)}
        </div>
      </MainLink>

      {hasSubmenu && isOpen && (
        <div className="mt-1 mx-2 bg-brand-gray bg-opacity-20 rounded-lg flex flex-col overflow-hidden">
          {item.submenu.map((subItem) => (
            <NavLink
              key={subItem.name}
              to={subItem.path}
              onClick={handleLinkClick}
              className={({ isActive }) =>
                `px-4 py-3 text-sm transition-colors ${isActive ? 'text-white font-semibold bg-brand-gray' : 'text-gray-300 hover:text-white hover:bg-brand-gray hover:bg-opacity-50'
                }`
              }
            >
              {subItem.name}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/category`);
        const result = await response.json();
        if (result.success && result.data) {
          const mappedData = result.data.map(item => ({
            name: item.name,
            path: `/categorydetail/${item.id}`,
            submenu: item.subcategories?.map(sub => ({
              name: sub.name,
              path: `/categorydetail/${sub.id}`
            })) || []
          }));
          setCategories(mappedData);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

  const navItems = [
    { name: 'Search', icon: FiSearch, path: '/search' },
    { name: 'Home', icon: FiHome, path: '/' },
    { name: 'Watchlist', icon: FiBookmark, path: '/watchlist' },
  ];

  const handleCloseMobile = () => {
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Mobile Top Header */}
      <div className="fixed top-0 left-0 w-full h-16 bg-brand-dark border-b border-gray-800 flex items-center justify-between px-4 z-40 md:hidden shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="p-2 -ml-2 text-white hover:bg-brand-gray rounded-lg transition-colors"
          >
            {isMobileOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
          <NavLink to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-accent flex items-center justify-center">
              <span className="text-white font-bold text-sm">CV</span>
            </div>
            <span className="text-white font-bold text-base">Christian Videos</span>
          </NavLink>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 md:hidden z-30"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-screen w-64 bg-brand-dark flex flex-col items-center py-8 z-50 border-r border-gray-800 transition-transform duration-300 md:translate-x-0 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
        <div className="w-full px-4 mb-8 hidden md:block">
          <NavLink
            to="/"
            onClick={handleCloseMobile}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-accent flex items-center justify-center">
                <span className="text-white font-bold text-lg">CV</span>
              </div>
              <span className="text-white font-bold text-lg">Christian Videos</span>
            </div>
          </NavLink>
        </div>

        {/* Mobile menu header inside sidebar if needed, but we have external header now.
            We can keep the logo for desktop as above.
        */}

        <nav className="flex flex-col items-start gap-2 w-full px-4 mt-8 md:mt-0">
          {navItems.map((item) => (
            <SidebarMenuItem key={item.name} item={item} onClose={handleCloseMobile} />
          ))}
        </nav>

        <div className="w-full px-8 my-8">
          <div className="border-t border-gray-700" />
        </div>

        <nav className="flex flex-col items-start gap-2 w-full px-4 flex-1 overflow-y-auto">
          <h3 className="text-gray-500 font-semibold uppercase text-sm px-4 mb-2 sticky top-0 bg-brand-dark py-2 z-10">Categories</h3>
          {categories.map((category) => (
            <SidebarMenuItem key={category.name} item={category} hideIcon={true} onClose={handleCloseMobile} />
          ))}
        </nav>

        <div className="mt-auto w-full px-4 pt-4">
          <SidebarMenuItem item={{ name: 'Sign In / Sign Up', icon: FiUser, path: '/auth' }} onClose={handleCloseMobile} />
        </div>
      </aside>
    </>
  );
};
export default Sidebar;
